import { EDatasourceType, ESdChangeReason, IDatasourceDto } from "@kuika/kuika-cl-model";
import Editor from "@monaco-editor/react";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import { HttpStatusCode } from "axios";
import { debounce } from "lodash";
import * as monacoEditor from "monaco-editor";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { LanguageServerService } from "../../../services/language-server-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { typescriptEditorOptions } from "../../../shared/utility/monaco-editor-config";
import { injectMonacoStyleFixes } from "../../../shared/utility/monaco-style-fixes";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import { AICodeGenerator, EAICodeGeneratorType } from "../../common/ai/ai-code-generator";
import { IPlannedCodeInsertion } from "../../common/ai/ai-code-insertion";
import { AiPendingEdit } from "../../common/ai/ai-pending-edit";
import { AiPendingEditBar } from "../../common/ai/ai-pending-edit-bar";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import ExternalLibrariesButton from "../../common/external-libraries-button/external-libraries-button";
import { TestRunner } from "../../common/test-runner/test-runner";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { CodeEditorControls, ICodeEditorControlsState } from "../code-editor-shell/code-editor-controls";
import { CodeEditorHeader } from "../code-editor-shell/code-editor-header";
import {
  codeEditorRenameTestIds,
  getOpeningDatasourceName,
  validateRenameField
} from "../code-editor-shell/code-editor-rename";
import { CodeEditorRenameTitle } from "../code-editor-shell/code-editor-rename-title";
import { CodeEditorSidePanel, ECodeEditorSideTab } from "../code-editor-shell/code-editor-side-panel";
import { CodeEditorSplitter } from "../code-editor-shell/code-editor-splitter";
import { CODE_EDITOR_THEME, defineCodeEditorTheme, watchCodeEditorTheme } from "../code-editor-shell/code-editor-theme";
import { CodeEditorToolbar } from "../code-editor-shell/code-editor-toolbar";
import { requestDatasourceReveal } from "./datasource-reveal";
import { CodeDatasourceSwitcherChip } from "./editor-switcher/code-datasource-switcher-chip";

const DEFAULT_CODE = `class NewClassName {
  static NewFunctionName(parameter1: string): string {
    // Your code here
    return parameter1;
  }
}
`;

interface ICreateJavaScriptActionState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Undo, wrapping, the font stepper, the caret and the markers — owned by `CodeEditorControls`. */
  controls: ICodeEditorControlsState;
  /** Which side tab is showing; Kuika AI is the only one this editor offers. */
  sideTab: ECodeEditorSideTab;
  /** Folded from the vertical grip, and dragged by it. */
  isSidePanelOpened: boolean;
  sidePanelWidth: number;
  /** The AI edit waiting for the user's confirmation; the editor still holds their own code. */
  pendingAiEdit: IPlannedCodeInsertion | null;
  /**
   * The committed name. The field mounts only while renaming, and the form hands back mounted
   * fields alone — so this, never the form, is what the hash, the save and the dialogs read.
   */
  datasourceName: string;
  isNameOnEdit: boolean;
}

interface ICreateJavaScriptActionProps {
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (showModal: boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

class CreateJavaScriptAction extends PureSdComponent<
  ICreateJavaScriptActionProps & IWithSDProps,
  ICreateJavaScriptActionState
> {
  formRef = React.createRef<FormInstance>();

  editorRef: monacoEditor.editor.IStandaloneCodeEditor | null = null;

  aiPendingEdit: AiPendingEdit = new AiPendingEdit({
    onChange: (pendingAiEdit: IPlannedCodeInsertion | null) => this.setState({ pendingAiEdit })
  });

  controls: CodeEditorControls = new CodeEditorControls({
    onChange: (controls: ICodeEditorControlsState) => this.setState({ controls })
  });

  codeThemeObserver: MutationObserver | undefined;
  diagnosticsAbortController: AbortController | null = null;

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateJavaScriptActionProps & IWithSDProps) {
    super(props);
    const datasourceName: string = getOpeningDatasourceName(
      props.editingDatasource,
      props.sd.activeCreatingDatasourceName
    );
    this.state = {
      saveStatus: "idle",
      controls: this.controls.state,
      sideTab: "coach",
      isSidePanelOpened: true,
      sidePanelWidth: 288,
      pendingAiEdit: null,
      datasourceName,
      // The create picker asks for the name first, so a new file opens named; only a file
      // with no name at all opens with the field.
      isNameOnEdit: !datasourceName
    };
  }

  componentDidMount() {
    this.loadDatasource();
  }

  componentDidUpdate(prevProps: ICreateJavaScriptActionProps & IWithSDProps) {
    if (this.props.editingDatasource?.id !== prevProps.editingDatasource?.id) {
      this.loadDatasource();
    }
    if (
      !this.props.editingDatasource &&
      this.props.sd.activeCreatingDatasourceName &&
      prevProps.sd.activeCreatingDatasourceName === undefined
    ) {
      this.seedName(this.props.sd.activeCreatingDatasourceName);
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  loadDatasource = () => {
    const { editingDatasource } = this.props;
    const name: string = getOpeningDatasourceName(editingDatasource, this.props.sd.activeCreatingDatasourceName);
    let code = editingDatasource?.code || DEFAULT_CODE;
    if (!editingDatasource?.code && name) {
      const className = name.charAt(0).toUpperCase() + name.slice(1);
      code = code.replace("NewClassName", className);
    }

    this.formRef.current?.setFieldsValue({ JavaScriptCode: code });
    this.seedName(name);
  };

  /** Opens on `name`, and takes the baseline once the state holds it. */
  seedName = (name: string): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: name });
    this.setState({ datasourceName: name, isNameOnEdit: !name }, (): void => {
      this.objectHash = this.getObjectHash();
    });
  };

  getObjectHash(): string {
    const code: unknown = this.formRef.current?.getFieldValue("JavaScriptCode");
    return JSON.stringify({ name: this.state.datasourceName, code });
  }

  handleStartRename = (): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: this.state.datasourceName });
    this.setState({ isNameOnEdit: true });
  };

  /** Commits the typed name once its rules — the async uniqueness check among them — pass. */
  handleConfirmRename = async (): Promise<string | undefined> => {
    const name: string | undefined = await validateRenameField(this.formRef.current, "DatasourceName");
    if (name === undefined) return undefined;
    this.setState({ datasourceName: name, isNameOnEdit: false });
    return name;
  };

  handleAbortRename = (): void => {
    this.formRef.current?.setFieldsValue({ DatasourceName: this.state.datasourceName });
    this.setState({ isNameOnEdit: false });
  };

  /** The name a save writes: the committed one, or — mid-rename — the typed one once it passes. */
  resolveNameForSave = async (): Promise<string | undefined> => {
    if (this.state.isNameOnEdit) return this.handleConfirmRename();
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a Datasource Name");
      return undefined;
    }
    return this.state.datasourceName;
  };

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }
    this.props.setShowModal(true);
  }

  nameValidator = async (_rule: unknown, value: string) => {
    if (!value) return;

    const existingDsName = this.props.sd?.datasources?.find(
      (ds) => ds.name.toLowerCase() === value.toLowerCase() && ds.id !== this.props.editingDatasource?.id
    );
    if (existingDsName) throw new Error("Datasource name already exists.");
  };

  saveDatasource = async (): Promise<void> => {
    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    const name: string | undefined = await this.resolveNameForSave();
    if (!name) {
      this.saveStatus.reset();
      return;
    }
    const ds = {
      name,
      datasourceType: EDatasourceType.JavaScriptAction,
      code: this.formRef.current?.getFieldValue("JavaScriptCode")
    } as IDatasourceDto;

    if (this.props.editingDatasource?.id) {
      ds.id = this.props.editingDatasource.id;
    }

    await DatasourceService.SaveDatasource(ds)
      .then(async (response): Promise<void> => {
        if (response.status !== HttpStatusCode.Ok) {
          // A resolved response that is not 200: the trailing reset used to rescue this branch.
          this.saveStatus.fail();
          return;
        }
        this.props.sd.refreshActions();
        await this.props.sd.refreshDatabases();
        // The ✓ goes up first and the editor leaves after it — see `SaveStatus`.
        // A close confirmation this save answered ("Update & close") steps aside at once, so
        // the ✓ on the button is what is seen before the editor leaves.
        this.props.setShowModal(false);
        this.saveStatus.succeed((): void => {
          // The list behind the editor lights the row up; the button has already said "Saved".
          requestDatasourceReveal({ name: ds.name });
          this.closeDataSource();
        });
      })
      .catch((err): void => {
        this.saveStatus.fail();
        KMainFunctions.displayWarningNotification(err.response?.data?.message ?? err.message);
      });
  };

  handleEditorBeforeMount = (monacoInstance: typeof monacoEditor) => {
    injectMonacoStyleFixes();
    // Monaco cannot read CSS variables, so the theme is declared here and re-declared
    // whenever the app's appearance flips.
    defineCodeEditorTheme(monacoInstance);
    this.codeThemeObserver = watchCodeEditorTheme(monacoInstance);
  };

  handleEditorMount = (editor: monacoEditor.editor.IStandaloneCodeEditor) => {
    this.editorRef = editor;
    this.aiPendingEdit.attach(monacoEditor, editor);
    this.controls.attach(monacoEditor, editor);
    const model = editor.getModel();
    if (model) {
      this.requestDiagnostics(model.getValue());
    }
  };

  /** The editor's own content listener writes the form field and refreshes diagnostics. */
  handleApplyPendingAiEdit = () => {
    this.aiPendingEdit.commit();
  };

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.aiPendingEdit.dispose();
    this.controls.dispose();
    this.codeThemeObserver?.disconnect();
    this.handleEditorChange.cancel();
    this.diagnosticsAbortController?.abort();
  }

  handleEditorChange = debounce((value: string | undefined) => {
    this.requestDiagnostics(value ?? "");
  }, 500);

  requestDiagnostics = async (value: string) => {
    const editor = this.editorRef;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;
    const monaco = (window as unknown as { monaco: typeof monacoEditor }).monaco;
    if (!monaco) return;

    this.diagnosticsAbortController?.abort();
    this.diagnosticsAbortController = new AbortController();

    try {
      const diagnostics = await LanguageServerService.GetDiagnostics(
        value,
        this.props.editingDatasource?.id ?? null,
        this.diagnosticsAbortController.signal,
        "typescript"
      );
      if (this.diagnosticsAbortController?.signal.aborted) return;

      const markers = diagnostics.map((d) => ({
        severity:
          d.severity === 3
            ? monaco.MarkerSeverity.Error
            : d.severity === 2
              ? monaco.MarkerSeverity.Warning
              : monaco.MarkerSeverity.Info,
        startLineNumber: d.startLineNumber + 1,
        startColumn: d.startColumn + 1,
        endLineNumber: d.endLineNumber + 1,
        endColumn: d.endColumn + 1,
        message: d.message,
        source: "Kuika Language Services"
      }));

      monaco.editor.setModelMarkers(model, "typescript", markers);
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== "AbortError") {
        console.error("TS LSP diagnostics error:", error);
      }
    }
  };

  handleFormatCode = async () => {
    const editor = this.editorRef;
    if (!editor) return;

    const model = editor.getModel();
    if (!model) return;

    const source = model.getValue();

    try {
      const [prettier, ts, estree] = await Promise.all([
        import("prettier/standalone"),
        import("prettier/plugins/typescript"),
        import("prettier/plugins/estree")
      ]);
      const tsPlugin = [ts.default ?? ts, estree.default ?? estree];

      const formatted = await prettier.format(source, {
        parser: "typescript",
        plugins: tsPlugin,
        printWidth: 120,
        tabWidth: 2,
        singleQuote: false,
        trailingComma: "all",
        semi: true
      });

      editor.executeEdits("prettier", [
        {
          range: model.getFullModelRange(),
          text: formatted
        }
      ]);

      this.formRef.current?.setFieldsValue({
        JavaScriptCode: formatted
      });
    } catch (e) {
      KMainFunctions.displayWarningNotification("Format failed: " + (e as Error).message);
      return;
    }

    this.controls.markFormatted();
  };

  handleParseMethods = async () => {
    const code = this.formRef.current?.getFieldValue("JavaScriptCode") || "";
    const response = await DatasourceService.ParseTypeScript(code);
    return response.data.methods || [];
  };

  handleExecute = async (methodName: string, args: unknown[], serverSide: boolean): Promise<string> => {
    const code = this.formRef.current?.getFieldValue("JavaScriptCode") || "";
    const response = await DatasourceService.ExecuteTypeScript({
      code,
      className: "",
      methodName,
      args,
      serverSide
    });

    if (serverSide) {
      return JSON.stringify(response.data.result, null, 2) ?? "undefined";
    }

    const { transpiledCode, className: cls } = response.data as {
      transpiledCode: string;
      className: string;
    };
    const fn = new Function(
      `${transpiledCode}\nreturn ${cls}.${methodName}(${args.map((_, i) => `arguments[${i}]`).join(", ")});`
    );
    const result = fn(...args);
    return JSON.stringify(result, null, 2) ?? "undefined";
  };

  render() {
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the editor is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";

    return (
      <Form
        name="dsModal"
        ref={this.formRef}
        onFinish={this.saveDatasource}
        layout="vertical"
        initialValues={{
          DatasourceName: this.props.editingDatasource?.name || "",
          JavaScriptCode: this.props.editingDatasource?.code || DEFAULT_CODE
        }}
        style={{ height: "100%" }}
      >
        <KuikaSpinnerSmall spinning={isSaving}>
          <div className="kuika__code-editor__page">
            <div className="kuika__code-editor__shell">
              <CodeEditorHeader
                title={this.state.datasourceName || "TypeScript Action"}
                renderTitle={(): React.ReactNode => (
                  <CodeEditorRenameTitle
                    identity={
                      <CodeDatasourceSwitcherChip
                        sd={this.props.sd}
                        setNextCallbackFunction={this.props.setNextCallbackFunction}
                        types={[EDatasourceType.JavaScriptAction]}
                        current={this.props.editingDatasource}
                        name={this.state.datasourceName}
                        entityLabel="TypeScript Action"
                        datasourceType={EDatasourceType.JavaScriptAction}
                        triggerTestId="typescriptActionSwitcherTrigger"
                        labelTestId="typescriptActionNameLabel"
                        onManageAll={() => this.handleCloseDatasource()}
                      />
                    }
                    isRenaming={this.state.isNameOnEdit}
                    canAbortRename={!!this.state.datasourceName}
                    entityLabel="TypeScript action"
                    fieldName="DatasourceName"
                    rules={[
                      { required: true, message: "Please enter a Datasource Name" },
                      { validator: this.nameValidator }
                    ]}
                    placeholder="Datasource name"
                    testIds={codeEditorRenameTestIds("typescriptActionName")}
                    onStartRename={this.handleStartRename}
                    onConfirmRename={(): void => void this.handleConfirmRename()}
                    onAbortRename={this.handleAbortRename}
                  />
                )}
                backLabel="Back to Datasources"
                saveLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
                saveStatus={this.state.saveStatus}
                isSaveDisabled={false}
                saveTestId="typescriptActionCreateButton"
                onClose={() => this.handleCloseDatasource()}
                closeTestId="closeCreateActionPanel"
              />

              <div className="kuika__code-editor__body">
                <div className="kuika__code-editor__main">
                  <CodeEditorToolbar
                    {...this.controls.toolbarBinding}
                    onFormat={this.handleFormatCode}
                    formatHint={
                      <div>
                        <p>How TIDY UP Formats Your Code:</p>
                        <ul style={{ paddingLeft: "16px", margin: "4px 0" }}>
                          <li>Uses 2 spaces for indentation</li>
                          <li>Ensures consistent brace placement</li>
                          <li>Adds proper spacing around operators</li>
                          <li>Standardizes method declaration spacing</li>
                        </ul>

                        {this.props.editingDatasource && (
                          <p style={{ marginTop: "8px" }}>
                            Formatting changes become permanent after you click UPDATE.
                          </p>
                        )}
                      </div>
                    }
                    trailing={
                      <>
                        <ExternalLibrariesButton
                          disabled
                          badge={{ content: "SOON", color: "orange" }}
                          libraries={[]}
                          packages={[]}
                          versions={[]}
                          hasMorePackages={false}
                          onOpen={() => {}}
                          onSearch={() => {}}
                          onExpand={() => {}}
                          onAdd={() => {}}
                          onDelete={() => {}}
                          onChangeVersion={() => {}}
                          onLoadMore={() => {}}
                        />
                      </>
                    }
                  />

                  {this.state.pendingAiEdit && (
                    <AiPendingEditBar
                      plan={this.state.pendingAiEdit}
                      subject="code"
                      onApply={this.handleApplyPendingAiEdit}
                      onDiscard={this.aiPendingEdit.discard}
                      onChangeMode={this.aiPendingEdit.setMode}
                    />
                  )}

                  <div className="kuika__code-editor__editor">
                    <Form.Item name="JavaScriptCode" noStyle>
                      <Editor
                        language="typescript"
                        theme={CODE_EDITOR_THEME}
                        beforeMount={this.handleEditorBeforeMount}
                        onMount={this.handleEditorMount as unknown as Parameters<typeof Editor>[0]["onMount"]}
                        onChange={this.handleEditorChange}
                        options={typescriptEditorOptions as unknown as Parameters<typeof Editor>[0]["options"]}
                      />
                    </Form.Item>
                  </div>

                  <TestRunner
                    onParseMethods={this.handleParseMethods}
                    onExecute={this.handleExecute}
                    showServerClientSplit={true}
                  />
                </div>

                <CodeEditorSplitter
                  orientation="vertical"
                  size={this.state.sidePanelWidth}
                  onSizeChange={(sidePanelWidth) => this.setState({ sidePanelWidth })}
                  minSize={240}
                  maxSize={520}
                  isCollapsed={!this.state.isSidePanelOpened}
                  onToggleCollapsed={(isCollapsed) => this.setState({ isSidePanelOpened: !isCollapsed })}
                />

                {this.state.isSidePanelOpened && (
                  <CodeEditorSidePanel
                    width={this.state.sidePanelWidth}
                    activeTab={this.state.sideTab}
                    onTabChange={(sideTab) => this.setState({ sideTab })}
                    parameterCount={0}
                    coachContent={
                      <AICodeGenerator
                        type={EAICodeGeneratorType.TypeScript}
                        datasourceId={this.props.editingDatasource?.id}
                        onApplyCode={this.aiPendingEdit.request}
                      />
                    }
                    // A function's arguments are typed into the test console, so there is no
                    // second pane to offer here.
                    parametersContent={null}
                  />
                )}
              </div>
            </div>
          </div>
        </KuikaSpinnerSmall>
        <CloseConfirmationModal
          itemName={this.state.datasourceName}
          closeType={"TypeScript Connection"}
          triggerModal={this.props.showModal}
          isLoading={isSaving}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </Form>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createJavaScriptAction = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateJavaScriptActionProps>(CreateJavaScriptAction));
export { createJavaScriptAction as CreateJavaScriptAction };
